import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { pushAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import DependentAccountsSection from '../components/DependentAccountsSection';

function AccountSettings() {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();

  // ── Profile (display name + email) ────────────────────────────────────────
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');

    const trimmedName = displayName.trim();
    const trimmedEmail = email.trim().toLowerCase();
    const payload = {};

    if (trimmedName && trimmedName !== user?.displayName) payload.displayName = trimmedName;
    if (trimmedEmail && trimmedEmail !== user?.email) payload.email = trimmedEmail;

    if (Object.keys(payload).length === 0) {
      setProfileError('No changes detected.');
      return;
    }
    if (payload.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
      setProfileError('Enter a valid email address.');
      return;
    }

    setProfileLoading(true);
    try {
      const result = await updateProfile(payload);
      if (result.success) {
        setProfileSuccess('Profile updated successfully!');
      } else {
        setProfileError(result.error || 'Update failed.');
      }
    } finally {
      setProfileLoading(false);
    }
  };

  // ── Password ───────────────────────────────────────────────────────────────
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  // ── Push Notifications ─────────────────────────────────────────────────────
  const [testNotifLoading, setTestNotifLoading] = useState(false);
  const [testNotifError, setTestNotifError] = useState('');
  const [testNotifSuccess, setTestNotifSuccess] = useState('');

  const handleTestNotification = async () => {
    setTestNotifError('');
    setTestNotifSuccess('');
    setTestNotifLoading(true);
    
    try {
      const response = await pushAPI.sendTestNotification();
      if (response.success) {
        setTestNotifSuccess(response.message || 'Test notification sent!');
      } else {
        setTestNotifError(response.error?.message || 'Failed to send test notification.');
      }
    } catch (err) {
      setTestNotifError(err.response?.data?.error?.message || 'Failed to send test notification.');
    } finally {
      setTestNotifLoading(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');

    if (newPassword !== confirmPassword) {
      setPwError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setPwError('New password must be at least 8 characters.');
      return;
    }
    if (currentPassword === newPassword) {
      setPwError('New password must differ from current password.');
      return;
    }

    setPwLoading(true);
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword });
      setPwSuccess('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwError(err.response?.data?.error?.message || 'Failed to change password. Please try again.');
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="auth-container" style={{ maxWidth: 560 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0 }}>Account Settings</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{ 
            fontSize: '0.9rem', 
            color: '#fff', 
            background: 'none', 
            border: 'none', 
            cursor: 'pointer', 
            padding: 0 
          }}
        >
          ← Back
        </button>
      </div>

      {/* ── Profile section ───────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ marginTop: 0 }}>👤 Profile</h3>
        <p style={{ color: '#888', fontSize: '0.9rem', margin: '0 0 1rem 0' }}>
          Username: <strong>{user?.username}</strong>
        </p>

        {profileError && <div className="error-message">{profileError}</div>}
        {profileSuccess && <div className="success-message">{profileSuccess}</div>}

        <form onSubmit={handleProfileSave}>
          <div className="form-group">
            <label htmlFor="displayName">Display Name</label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => { setDisplayName(e.target.value); setProfileError(''); setProfileSuccess(''); }}
              placeholder="Your public display name"
              maxLength={50}
              disabled={profileLoading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setProfileError(''); setProfileSuccess(''); }}
              placeholder="your@email.com"
              disabled={profileLoading}
            />
          </div>
          <button type="submit" className="submit-button" disabled={profileLoading}>
            {profileLoading ? 'Saving…' : 'Save Profile'}
          </button>
        </form>
      </div>

      {/* ── Password section ──────────────────────────────────────────────── */}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>🔑 Change Password</h3>

        {pwError && <div className="error-message">{pwError}</div>}
        {pwSuccess && <div className="success-message">{pwSuccess}</div>}

        <form onSubmit={handlePasswordSave}>
          <div className="form-group">
            <label htmlFor="currentPassword">Current Password</label>
            <div className="password-field">
              <input
                id="currentPassword"
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => { setCurrentPassword(e.target.value); setPwError(''); }}
                required
                disabled={pwLoading}
              />
              <button
                type="button"
                className="toggle-pw"
                onClick={() => setShowCurrent(!showCurrent)}
                tabIndex="-1"
              >
                {showCurrent ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <div className="password-field">
              <input
                id="newPassword"
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setPwError(''); }}
                required
                disabled={pwLoading}
                minLength="8"
              />
              <button
                type="button"
                className="toggle-pw"
                onClick={() => setShowNew(!showNew)}
                tabIndex="-1"
              >
                {showNew ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div className="password-field">
              <input
                id="confirmPassword"
                type={showNew ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setPwError(''); }}
                required
                disabled={pwLoading}
                minLength="8"
              />
            </div>
          </div>

          <button type="submit" className="submit-button" disabled={pwLoading}>
            {pwLoading ? 'Saving…' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* ── Push Notifications section ────────────────────────────────────── */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <h3 style={{ marginTop: 0 }}>🔔 Push Notifications</h3>
        <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1rem' }}>
          Test if your browser and mobile push notifications are working correctly.
        </p>

        {testNotifError && <div className="error-message">{testNotifError}</div>}
        {testNotifSuccess && <div className="success-message">{testNotifSuccess}</div>}

        <button 
          type="button" 
          className="submit-button" 
          onClick={handleTestNotification}
          disabled={testNotifLoading}
        >
          {testNotifLoading ? 'Sending…' : '📬 Send Test Notification'}
        </button>
      </div>

      {/* ── Dependent Accounts section ────────────────────────────────────── */}
      <DependentAccountsSection user={user} />

      <style jsx="true">{`
        .password-field {
          position: relative;
          display: flex;
          align-items: center;
        }
        .password-field input {
          flex: 1;
          padding-right: 2.5rem;
        }
        .toggle-pw {
          position: absolute;
          right: 0.6rem;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1.1rem;
          line-height: 1;
          padding: 0;
          color: #555;
        }
        .toggle-pw:hover { color: #000; }
        .success-message {
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
          padding: 0.75rem 1rem;
          border-radius: 4px;
          margin-bottom: 1rem;
        }
        .link-button {
          background: none;
          border: none;
          color: #007bff;
          cursor: pointer;
          font-size: 0.95rem;
          padding: 0;
          text-decoration: underline;
        }
        .link-button:hover { color: #0056b3; }
      `}</style>
    </div>
  );
}

export default AccountSettings;
